import { DefaultChatTransport, type UIMessage, type UIMessageChunk } from 'ai'

import envConfig from '~/configs/env'
import type { PageContext, ViewedEntity } from '~/data/api/client/assistant'
import { getAccessTokenSilently } from '~/data/api/client/security'

import { createPacingTransform } from './pacing'

/** A non-2xx answer to a chat request, keeping the status the AI SDK transport would otherwise discard. */
class AssistantRequestError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'AssistantRequestError'
    this.status = status
  }
}

/**
 * Why a turn failed, as far as the composer cares: `quota` (429, the daily allowance is spent), `credit` (402,
 * the organization is out of credit), `busy` (409, another turn is streaming in this conversation), `access`
 * (403/404), or anything else.
 */
type ChatFailure = 'quota' | 'credit' | 'busy' | 'access' | 'generic'

export function classifyChatError(error: unknown): ChatFailure {
  if (!(error instanceof AssistantRequestError)) return 'generic'
  switch (error.status) {
    case 429:
      return 'quota'
    case 402:
      return 'credit'
    case 409:
      return 'busy'
    case 403:
    case 404:
      return 'access'
    default:
      return 'generic'
  }
}

function conversationMessagesUrl(conversationId: string): string {
  return `${envConfig.serviceUrls.assistant}/api/v1/assistant/conversations/${conversationId}/messages`
}

type TransportOptions = {
  conversationId: string
  organizationId: string
  /** The entities the page declares at the moment a turn is sent (see `useViewedEntity`). */
  getViewedEntities: () => ViewedEntity[]
}

/**
 * The UI message stream transport for one conversation. Requests carry the Auth0 access token and the viewed
 * organization, the same headers the RTK Query clients send. The body is the AI SDK default
 * (`{ id, messages, trigger, messageId }`) plus the page the question was asked from: its route, title and the
 * entities it shows. Text and reasoning arrive paced word by word.
 */
/** The default transport with its parsed chunks re-timed word by word (see `createPacingTransform`). */
class PacedChatTransport<MESSAGE extends UIMessage> extends DefaultChatTransport<MESSAGE> {
  protected processResponseStream(stream: ReadableStream<Uint8Array>): ReadableStream<UIMessageChunk> {
    return super.processResponseStream(stream).pipeThrough(createPacingTransform())
  }
}

export function createAssistantTransport<MESSAGE extends UIMessage = UIMessage>({
  conversationId,
  organizationId,
  getViewedEntities
}: TransportOptions) {
  return new PacedChatTransport<MESSAGE>({
    api: conversationMessagesUrl(conversationId),
    headers: async () => ({
      Authorization: `Bearer ${await getAccessTokenSilently()}`,
      'X-Organization-Id': organizationId
    }),
    body: () => {
      const pageContext: PageContext = {
        route: window.location.pathname,
        pageTitle: document.title,
        entities: getViewedEntities()
      }
      return { pageContext }
    },
    fetch: fetchRejectingErrorStatus
  })
}

async function fetchRejectingErrorStatus(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init)
  if (response.ok) return response
  const body = await response.text().catch(() => '')
  throw new AssistantRequestError(response.status, body || response.statusText)
}
